// eslint-disable-next-line import/no-extraneous-dependencies
import { ToastId, UseToastOptions } from '@chakra-ui/react';
import axios, { AxiosError, AxiosInstance } from 'axios';
import Keycloak from 'keycloak-js';

import { i18n } from '@cccom/shared/i18n';
import { arrayToObject, toastAttributes } from '@cccom/shared/utils/common';
import { ACRValue } from '@cccom/auth/keycloak';
import { log } from '@cccom/shared/logging';

import { ApiError } from '../../types';

export function apiErrorHandler(
  error: AxiosError<ApiError>,
  toast: (options?: UseToastOptions) => ToastId
) {
  if (!error.response) return;

  const showToastHandler = (toastDescription: string) => {
    toast({
      ...toastAttributes,
      id: Date.now().toString(),
      description: toastDescription,
      status: 'error',
    });
  };

  const i18nMessageHandler = ({
    i18nMessage,
    message,
  }: Pick<ApiError, 'i18nMessage' | 'message'>) => {
    if (!i18nMessage) return message;

    const { variables, key } = i18nMessage;

    if (!variables)
      return i18n.t([key, message ?? 'common.errors.something_went_wrong']);

    return i18n.t(
      [key, message ?? 'common.errors.something_went_wrong'],
      arrayToObject(variables)
    );
  };

  const apiError = error.response.data;
  if (!apiError || !apiError.message) {
    showToastHandler(i18n.t('common.errors.something_went_wrong'));
    return;
  }

  if (apiError.errors?.length) return;

  if (apiError.i18nMessage) {
    showToastHandler(i18nMessageHandler(apiError));
    return;
  }

  showToastHandler(apiError.message);
}

export function addResponseInterceptor({
  axiosInstance,
  toast,
}: {
  axiosInstance: AxiosInstance;
  keycloak: Keycloak;
  acr: ACRValue;
  toast: (options?: UseToastOptions) => ToastId;
}) {
  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      log.error(
        `Http Error: Error occurred during api call: ${error?.config?.url} :`,
        error
      );
      if (axios.isAxiosError(error)) {
        apiErrorHandler(error, toast);
      }
      throw error;
    }
  );
}
